import type { ReactNode } from "react";

// Shared form-control styling for the dialogs. Pulled out so the
// request and schedule forms read consistently and we have one place
// to change input borders / focus rings.
//
// Heights are unified to 40px (h-10) on inputs and selects so a row
// of mixed fields lines up cleanly. Textareas use textareaCls instead
// — they grow with `rows` and can't have a fixed height.

export const inputCls =
  "h-10 w-full rounded-md border border-slate-300 px-3 text-sm placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

export const selectCls =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 pr-8 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

export const textareaCls =
  "w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

export function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

// Single submit button. Dismiss is handled by the X in the dialog
// header, so we don't render a redundant Close/Cancel here.
//
// `onCancel` kept in the signature (unused) so callers passing it
// from older code don't break; safe to remove from callers next pass.
export function DialogActions({
  onCancel: _onCancel,
  submitting,
  submitLabel,
  submittingLabel = "Saving...",
  disabled,
}: {
  onCancel?: () => void;
  submitting: boolean;
  submitLabel: string;
  submittingLabel?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-3 pt-2">
      <button
        type="submit"
        disabled={submitting || disabled}
        className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
      >
        {submitting ? submittingLabel : submitLabel}
      </button>
    </div>
  );
}
